import React, { useState } from "react";
import { Redirect, useHistory } from "react-router";
import { agent } from "../agent";
import PaymentMake3DSPage from "../pages/PaymentMake3DS";
import { URL_PATHS } from "../routes";
import { PaymentType } from "../generated/lib-domain/paymentZeus";
import {
    IAPPaymentMethod,
    OtherPaymentMethod,
    PaymentMethod
} from "../generated/lib-domain/iap";
import {
    PaymentMethodEnum,
    Prefecture,
    SubscriptionEnum,
    UserAddress,
    PostThreeDSecureEnrollResponseBody
} from "../generated/UserAPIAgent";
import { useDispatch, useSelector } from "react-redux";
import { myDataActions } from "../store/myData";
import { paymentActions } from "../store/payment";
import { AppState } from "../store";
import { UserStatus } from "../generated/lib-domain/user";
import { PAYMENT_PLANS } from "../../../lib-domain/dist/lib/paymentPlan";
import { Message } from "../types/common";
import { useOnceCall } from "../custom-hooks/once-call";
import { smartAlert } from "../lib/confirm-alert";
import { startThreeDSecureEnroll, handle3DSError } from "../lib/zeus-3ds";
import type { ThreeDSecureAuthResult } from "../components/ThreeDSecureAuth/ThreeDSecureAuth";
import { tokenZeus } from "../lib/zeus";

interface PaymentMake3DSState {
    paymentMakeIsLoading: boolean;
    paymentMakeIsFinished: boolean;
    registerAddressIsLoading: boolean;
    userAddress: UserAddress | null;
    isRepeatFreeUser: boolean;
    show3DSAuth: boolean;
    enrollResponse: PostThreeDSecureEnrollResponseBody | null;
    threeDSProcessing: boolean;
}

const initialState: PaymentMake3DSState = {
    paymentMakeIsLoading: false,
    paymentMakeIsFinished: false,
    registerAddressIsLoading: false,
    userAddress: null,
    isRepeatFreeUser: false,
    show3DSAuth: false,
    enrollResponse: null,
    threeDSProcessing: false
};

const PaymentMake3DSContainer = () => {
    const [paymentMake, setPaymentMake] = useState(initialState);
    const payment = useSelector((state: AppState) => state.payment);
    const user = useSelector((state: AppState) => state.myData.user);
    const dispatch = useDispatch();
    const history = useHistory();

    useOnceCall(() => {
        (async () => {
            try {
                const [paymentStatus, address] = await Promise.all([
                    agent.getPaymentStatus(),
                    user.status === UserStatus.Created
                        ? null
                        : agent.getUserAddress()
                ]);
                setPaymentMake((prev) => ({
                    ...prev,
                    userAddress: address || null,
                    isRepeatFreeUser:
                        paymentStatus.paymentType === PaymentType.Repeat &&
                        paymentStatus.nextPaymentDate == null &&
                        paymentStatus.paidFinishDate == null
                }));
            } catch (e) {
                console.error(e);
                await smartAlert("決済情報の取得に失敗しました。");
            }
        })();
    });

    const isChangeCardPlanPaidUser = (): boolean => {
        if (
            !payment.status?.currentPaymentPlan ||
            !payment.selectedPayment ||
            payment.status?.primaryPaymentMethod !==
                OtherPaymentMethod.CREDIT_CARD
        ) {
            return false;
        }
        const foundPlan = PAYMENT_PLANS.find(
            (plan) => plan.id === payment.selectedPayment.paymentPlanId
        );
        if (!foundPlan) return false;
        const selectedSubscription: SubscriptionEnum = foundPlan.subscription;
        return (
            payment.status.currentPaymentPlan.subscription !==
            selectedSubscription
        );
    };

    async function onSubmitAddress(
        zip: string,
        pref: Prefecture,
        city: string,
        address: string,
        building?: string
    ): Promise<void> {
        setPaymentMake((prev) => ({ ...prev, registerAddressIsLoading: true }));
        try {
            await agent.registerPaymentAddress({
                zip,
                pref,
                city,
                address,
                building
            });
            await agent.postPaymentZeusSubscribe({
                currentPaymentState: {
                    paymentType: payment.status.paymentType as PaymentType,
                    paymentPlanId: payment.status.currentPaymentPlan
                        ? payment.status.currentPaymentPlan.id
                        : null,
                    paymentMethod:
                        (payment.status
                            .primaryPaymentMethod as PaymentMethodEnum) || null
                },
                newPayment: payment.selectedPayment
            });
            dispatch(myDataActions.myDataShouldLoadSet(true));
            setPaymentMake((prev) => ({
                ...prev,
                registerAddressIsLoading: false
            }));
            history.push(URL_PATHS.paymentMakeFinish());
        } catch (error) {
            setPaymentMake((prev) => ({
                ...prev,
                registerAddressIsLoading: false
            }));
            await smartAlert("有料会員情報登録に失敗しました。");
        }
    }

    async function onSubmitCreditCardInfo(creditCard: {
        cardNumber: string;
        expiresMonth: string;
        expiresYear: string;
        securityNo: string;
    }): Promise<void> {
        setPaymentMake((prev) => ({ ...prev, paymentMakeIsLoading: true }));

        try {
            // カード情報をトークン化
            const tokenRes = await tokenZeus({
                cardNumber: creditCard.cardNumber,
                expiresMonth: creditCard.expiresMonth,
                expiresYear: creditCard.expiresYear,
                securityNo: creditCard.securityNo
            });

            if (tokenRes.status !== "success") {
                // ZEUSからのエラーメッセージを表示
                setPaymentMake((prev) => ({
                    ...prev,
                    paymentMakeIsLoading: false
                }));
                await smartAlert(
                    tokenRes.message || "カード情報の処理に失敗しました。"
                );
                return;
            }

            // IOS_IAPユーザーはtransferToCard APIで処理するため3DS不要
            if (
                payment.status.primaryPaymentMethod === IAPPaymentMethod.IOS_IAP
            ) {
                dispatch(paymentActions.paymentTokenSet(tokenRes.tokenKey));
                setPaymentMake((prev) => ({
                    ...prev,
                    paymentMakeIsLoading: false
                }));
                history.push(URL_PATHS.confirmTransition());
                return;
            }

            // 3DS認証開始（0円でカード妥当性チェック）
            const enrollResponse = await startThreeDSecureEnroll({
                tokenKey: tokenRes.tokenKey
            });

            // enrollがエラーの場合
            if (!enrollResponse.success) {
                setPaymentMake((prev) => ({
                    ...prev,
                    paymentMakeIsLoading: false
                }));
                const errorHandlingResult = handle3DSError(
                    enrollResponse.errorCode || "3DS999",
                    enrollResponse.errorMessage
                );
                await smartAlert(errorHandlingResult.displayMessage);
                return;
            }

            // 3DSモーダルを表示
            setPaymentMake((prev) => ({
                ...prev,
                enrollResponse,
                show3DSAuth: true,
                paymentMakeIsLoading: false,
                threeDSProcessing: true
            }));
        } catch (error) {
            console.error(error);
            setPaymentMake((prev) => ({
                ...prev,
                paymentMakeIsLoading: false
            }));
            await smartAlert("3DS認証の開始に失敗しました。");
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handle3DSAuthComplete = async (_result: ThreeDSecureAuthResult) => {
        try {
            // termUrlコールバック内でAuthReq→PayReqまで実行済みのため、
            // ここでは追加のAPI呼び出しは不要。完了処理に直接進む。

            if (user.status === UserStatus.Created) {
                // Webから会員登録して有料登録を行うケース
                setPaymentMake((prev) => ({
                    ...prev,
                    show3DSAuth: false,
                    threeDSProcessing: false
                }));
                dispatch(myDataActions.myDataShouldLoadSet(true));
                history.push(URL_PATHS.installApp());
                return;
            }

            if (payment.isChangeCardInfoInProgress) {
                // クレジットカード情報の変更
                setPaymentMake((prev) => ({
                    ...prev,
                    show3DSAuth: false,
                    threeDSProcessing: false
                }));
                dispatch(myDataActions.myDataShouldLoadSet(true));
                history.push(URL_PATHS.creditCardInfoChangeFinish());
                return;
            }

            if (paymentMake.isRepeatFreeUser || isChangeCardPlanPaidUser()) {
                // 過去に有料登録したことある現無料会員 or サブスクリプション変更する有料会員
                // 3DS認証（0円決済）完了後、プラン変更・実決済処理を実行
                try {
                    await agent.postPaymentZeusSubscribe({
                        currentPaymentState: {
                            paymentType: payment.status
                                .paymentType as PaymentType,
                            paymentPlanId:
                                payment.status.currentPaymentPlan?.id || null,
                            paymentMethod: payment.status
                                .primaryPaymentMethod as PaymentMethodEnum
                        },
                        newPayment: payment.selectedPayment
                    });
                    setPaymentMake((prev) => ({
                        ...prev,
                        show3DSAuth: false,
                        threeDSProcessing: false
                    }));
                    dispatch(myDataActions.myDataShouldLoadSet(true));
                    if (paymentMake.isRepeatFreeUser) {
                        history.push(URL_PATHS.paymentMakeFinish());
                    } else {
                        history.push(URL_PATHS.subscriptionChangeFinish());
                    }
                    return;
                } catch (error) {
                    console.error(error);
                    setPaymentMake((prev) => ({
                        ...prev,
                        show3DSAuth: false,
                        threeDSProcessing: false
                    }));
                    await smartAlert("決済処理に失敗しました。");
                    return;
                }
            }

            if (payment.status.paymentType === PaymentType.First) {
                // アプリから登録した無料会員 → 住所入力へ
                setPaymentMake((prev) => ({
                    ...prev,
                    show3DSAuth: false,
                    threeDSProcessing: false,
                    paymentMakeIsFinished: true
                }));
                return;
            }

            if (
                payment.status.primaryPaymentMethod === IAPPaymentMethod.IOS_IAP
            ) {
                // IAP決済からクレジットカード決済への変更
                setPaymentMake((prev) => ({
                    ...prev,
                    show3DSAuth: false,
                    threeDSProcessing: false
                }));
                history.push(URL_PATHS.confirmTransition());
                return;
            }
        } catch (error) {
            console.error(error);
            setPaymentMake((prev) => ({
                ...prev,
                show3DSAuth: false,
                threeDSProcessing: false
            }));
            await smartAlert("決済処理に失敗しました。");
        }
    };

    const handle3DSAuthError = async (error: {
        errorCode?: string;
        errorMessage?: string;
    }) => {
        setPaymentMake({
            ...paymentMake,
            show3DSAuth: false,
            threeDSProcessing: false
        });

        const errorHandlingResult = handle3DSError(
            error.errorCode || "3DS999",
            error.errorMessage
        );
        await smartAlert(errorHandlingResult.displayMessage);
    };

    if (!payment.inProgress) {
        return <Redirect to={URL_PATHS.paymentInfo()} />;
    }

    const campaigns = payment.status.cardPaymentPlans.find(
        (plan) => plan.paymentPlanId === payment.selectedPayment?.paymentPlanId
    )?.campaigns;
    const campaignMessage: Message | null =
        campaigns &&
        campaigns.length > 0 &&
        (campaigns[0].title !== "" || campaigns[0].description !== "")
            ? {
                  title: campaigns[0].title,
                  description: campaigns[0].description
              }
            : null;

    return (
        <PaymentMake3DSPage
            onSubmitCreditCardInfo={onSubmitCreditCardInfo}
            onSubmitAddress={onSubmitAddress}
            isChangeCreditCardInfo={payment.isChangeCardInfoInProgress}
            paymentMakeIsLoading={paymentMake.paymentMakeIsLoading}
            paymentMakeIsFinished={paymentMake.paymentMakeIsFinished}
            registerAddressIsLoading={paymentMake.registerAddressIsLoading}
            paymentInfoState={{
                paymentType: payment.status.paymentType,
                orderedAmount:
                    payment.selectedPayment?.firstAmount ||
                    payment.selectedPayment?.amount,
                primaryPaymentMethod: payment.status
                    .primaryPaymentMethod as PaymentMethod,
                cardPaymentPlan: payment.status.cardPaymentPlans.find(
                    (plan) =>
                        plan.paymentPlanId ===
                        payment.selectedPayment?.paymentPlanId
                )
            }}
            userAddress={paymentMake.userAddress}
            userStatus={user.status}
            campaignMessage={campaignMessage}
            show3DSAuth={paymentMake.show3DSAuth}
            sessionId={paymentMake.enrollResponse?.sessionId || ""}
            md={paymentMake.enrollResponse?.md || ""}
            termUrl={paymentMake.enrollResponse?.termUrl || ""}
            iframeUrl={paymentMake.enrollResponse?.iframeUrl || ""}
            clientIp={paymentMake.enrollResponse?.clientIp || ""}
            onAuthComplete={handle3DSAuthComplete}
            onAuthError={handle3DSAuthError}
            threeDSProcessing={paymentMake.threeDSProcessing}
        />
    );
};

export default PaymentMake3DSContainer;
